/**
 * CONTEXTO DA TELA - o que uma pagina declara ao Rimati, e como e conferido.
 *
 * Duas regras valem em todo este arquivo:
 *
 * 1. So entra o que a pagina DECLARA em formato fechado: rota, modulo, um
 *    registro em foco (tipo + id), um periodo em foco e, no maximo, um rascunho
 *    num esquema versionado e estrito. Nenhum texto livre da tela vira fato:
 *    nomes, rotulos de conta e totais sao resolvidos pelo servidor a partir dos
 *    ids, e o que o navegador escreve em `display` nunca sai da propria tela.
 * 2. O mesmo validador roda nos dois lados. A tela o usa para nunca enviar um
 *    contexto invalido; o servidor o usa porque a tela pode ter sido adulterada.
 *    Um validador so, sem biblioteca, para o contrato nao divergir.
 *
 * O que NAO esta aqui: a conferencia por id e empresa (servidor, `screen-context/`)
 * e os alvos de ajuda e de preenchimento (entregas A3 e A4). Os tipos
 * `ScreenTarget` e `capabilities` ja existem para elas se encaixarem sem mudar
 * o contrato.
 */

export const SCREEN_CONTEXT_VERSION = 1 as const;

/** Esquemas de rascunho aceitos. Acrescentar um e uma decisao de contrato, nao de pagina. */
export const SCREEN_DRAFT_SCHEMAS = ['pos.cart.v1'] as const;
export type ScreenDraftSchema = (typeof SCREEN_DRAFT_SCHEMAS)[number];

export const SCREEN_RECORD_KINDS = [
  'contact',
  'opportunity',
  'appointment',
  'sale',
  'cashSession',
  'product',
  'purchase',
] as const;
export type ScreenRecordKind = (typeof SCREEN_RECORD_KINDS)[number];

/** Um campo ou controle que a tela sabe apontar (usado por "como faco", A3). */
export interface ScreenTarget {
  /** Estavel, em notacao de ponto: `pos.payment.0.installments`. */
  id: string;
  /** Rotulo como aparece na tela. Texto do navegador: serve para exibir, nunca como fato. */
  label: string;
  /** O que precisa estar aberto para o alvo existir. */
  requires?: string[];
  /** Qual alvo abre o `requires`. */
  openedBy?: string;
  /** Alvo que precisa ter certo valor para este existir (Parcelas so com Cartao). */
  dependsOn?: { target: string; value: string };
  /** Desabilitado agora e por que. */
  disabledReason?: string;
}

export interface ScreenPeriod {
  /** AAAA-MM-DD, no fuso da empresa. */
  from: string;
  to: string;
}

export interface ScreenContextDto {
  v: typeof SCREEN_CONTEXT_VERSION;
  /** Caminho da tela, sem busca nem fragmento. */
  route: string;
  /** Chave do catalogo `SCREEN_MODULES`. */
  module: string;
  view?: string;
  record?: { kind: ScreenRecordKind; id: string } | null;
  period?: ScreenPeriod | null;
  draft?: { schema: ScreenDraftSchema; data: unknown } | null;
  /** A pessoa pediu "conversar sem o contexto": so rota e modulo seguem. */
  contextOff?: true;
  targets: ScreenTarget[];
  capabilities: {
    /** Esquemas que a tela aceitaria como alteracao proposta (A4). */
    patchSchemas: ScreenDraftSchema[];
    /** Confirmacoes nativas que a tela oferece (A4). */
    confirmTools: string[];
  };
}

/** O que a tela guarda: o contexto enviavel e o texto so para exibir ao proprio usuario. */
export interface ScreenPublication {
  context: ScreenContextDto;
  display: { title: string; summary: string };
}

/** Rascunho `pos.cart.v1`: o carrinho do PDV, so com valores tipados. */
export interface PosCartV1 {
  /** Local de estoque do carrinho (chave de negocio do estoque, nao um rotulo livre). */
  location: string;
  lines: Array<{
    productId: string;
    variantId: string;
    quantity: number;
    discountBps: number;
  }>;
  customerRef: string | null;
  /** Total que a tela exibe. O servidor recalcula e nunca o usa como fato. */
  displayedTotalCents: number;
  /** AV4: desconto no total da venda, em reais (centavos). Exclusivo com `discountTotalBps`. */
  discountTotalCents?: number;
  /** AV4: desconto no total da venda, em pontos-base (1% = 100). */
  discountTotalBps?: number;
  payments: Array<{
    method: 'cash' | 'pix' | 'card' | 'credit' | 'fiado';
    accountId: string | null;
    cardMethodId: string | null;
    amountCents: number | null;
    tenderedCents: number | null;
    installments: number;
    received: boolean;
    creditId: string | null;
  }>;
}

/* -------------------------------------------------------------------------- */
/* Catalogo de telas                                                           */
/* -------------------------------------------------------------------------- */

export interface ScreenModuleEntry {
  module: string;
  title: string;
  /** Caminhos que pertencem a esta tela (igual ou prefixo seguido de `/`). */
  routes: readonly string[];
}

/**
 * As telas que o Rimati reconhece pelo nome. O titulo vem DAQUI, e nao do
 * navegador, quando o servidor grava "Agora em: ...". Uma rota que nao esta na
 * lista continua funcionando (sem nome, sem marco).
 */
export const SCREEN_MODULES: readonly ScreenModuleEntry[] = [
  { module: 'inicio', title: 'Início', routes: ['/'] },
  { module: 'vendas.pdv', title: 'PDV e vendas', routes: ['/m/vendas/pdv'] },
  { module: 'balcao.pdv', title: 'Meu PDV · Balcão', routes: ['/m/balcao/pdv'] },
  { module: 'caixa.sessoes', title: 'Caixa', routes: ['/m/caixa/sessoes'] },
  { module: 'estoque.saldos', title: 'Estoque', routes: ['/m/estoque/saldos'] },
  {
    module: 'estoque.conferencias',
    title: 'Conferência de estoque',
    routes: ['/m/estoque/conferencias'],
  },
  { module: 'catalogo.produtos', title: 'Produtos', routes: ['/m/catalogo/produtos'] },
  { module: 'crm.contatos', title: 'Clientes', routes: ['/m/crm/contatos', '/contatos'] },
  {
    module: 'crm.oportunidades',
    title: 'Oportunidades',
    routes: ['/m/crm/oportunidades', '/oportunidades'],
  },
  { module: 'agenda.agenda', title: 'Agenda', routes: ['/m/agenda/agenda', '/agenda'] },
  {
    module: 'atendimento.conversas',
    title: 'Conversas',
    routes: ['/m/atendimento/conversas', '/conversas'],
  },
  { module: 'compras.pedidos', title: 'Compras', routes: ['/m/compras/pedidos'] },
  { module: 'financas.gestao', title: 'Gestão financeira', routes: ['/m/financas/gestao'] },
  { module: 'financas.despesas', title: 'Despesas', routes: ['/m/financas/despesas'] },
  { module: 'recebiveis.agenda', title: 'Recebíveis', routes: ['/m/recebiveis/agenda'] },
  {
    module: 'pedidos-online.pedidos',
    title: 'Pedidos online',
    routes: ['/m/pedidos-online/pedidos'],
  },
  { module: 'posvenda.casos', title: 'Pós-venda', routes: ['/m/posvenda/casos'] },
  { module: 'indicadores.painel', title: 'Indicadores', routes: ['/m/indicadores/painel'] },
  { module: 'resultados', title: 'Resultados', routes: ['/resultados'] },
  { module: 'rotinas.execucoes', title: 'Rotinas', routes: ['/m/rotinas/execucoes'] },
  { module: 'formularios.definicoes', title: 'Formulários', routes: ['/m/formularios/definicoes'] },
  { module: 'projetos.painel', title: 'Projetos', routes: ['/m/projetos/painel'] },
  { module: 'documentos.biblioteca', title: 'Documentos', routes: ['/m/documentos/biblioteca'] },
  { module: 'loja.editor', title: 'Loja virtual', routes: ['/m/loja/editor'] },
  { module: 'ferramentas', title: 'Ferramentas', routes: ['/modulos'] },
  { module: 'empresa', title: 'Minha empresa', routes: ['/minha-empresa'] },
  { module: 'configuracoes', title: 'Configurações', routes: ['/configuracoes'] },
];

/** Normaliza o caminho: sem busca, sem fragmento, sem barra final. */
export function normalizeScreenRoute(route: string): string {
  const path = route.split(/[?#]/)[0]!.replace(/\/+$/, '');
  return path || '/';
}

/** A tela do catalogo a que a rota pertence (a rota mais especifica vence). */
export function screenModuleForRoute(
  route: string,
): { entry: ScreenModuleEntry; route: string } | null {
  const path = normalizeScreenRoute(route);
  let best: { entry: ScreenModuleEntry; route: string } | null = null;
  for (const entry of SCREEN_MODULES)
    for (const candidate of entry.routes) {
      const match =
        candidate === '/' ? path === '/' : path === candidate || path.startsWith(`${candidate}/`);
      if (match && (!best || candidate.length > best.route.length))
        best = { entry, route: candidate };
    }
  return best;
}

export function screenModuleByKey(module: string): ScreenModuleEntry | null {
  return SCREEN_MODULES.find((entry) => entry.module === module) ?? null;
}

/* -------------------------------------------------------------------------- */
/* Validador (um so, para a tela e para o servidor)                            */
/* -------------------------------------------------------------------------- */

export const SCREEN_LIMITS = {
  targets: 60,
  label: 60,
  reason: 120,
  lines: 200,
  payments: 10,
  routeLength: 160,
  periodDays: 400,
} as const;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const OBJECT_ID = /^[0-9a-f]{24}$/i;
const TARGET_ID = /^[a-z][a-z0-9]*(\.[a-z0-9_-]+)+$/;
const MODULE_KEY = /^[a-z][a-z0-9-]*(\.[a-z0-9-]+)*$/;
const REQUIRES = /^(dialog|details|tab):[a-z0-9_-]{1,40}$/;
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const ROUTE = /^\/[A-Za-z0-9/_-]*$/;
const TOOL = /^[a-z][a-z0-9-]*\.[a-z][a-z0-9-]*$/;

/** Formato do identificador por tipo de registro (produto e caixa usam UUID; o resto, ObjectId). */
export function screenRecordIdPattern(kind: ScreenRecordKind): RegExp {
  return kind === 'product' || kind === 'cashSession' || kind === 'sale' || kind === 'purchase'
    ? UUID
    : OBJECT_ID;
}

type Parsed<T> = { ok: true; value: T } | { ok: false; error: string };
const fail = (error: string): { ok: false; error: string } => ({ ok: false, error });
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

function onlyKeys(value: Record<string, unknown>, allowed: readonly string[]): string | null {
  const extra = Object.keys(value).find((key) => !allowed.includes(key));
  return extra ? `campo desconhecido: ${extra}` : null;
}

function dayNumber(value: string): number | null {
  if (!DATE.test(value)) return null;
  const date = new Date(`${value}T00:00:00Z`);
  return Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== value
    ? null
    : Math.round(date.getTime() / 86_400_000);
}

function isInt(value: unknown, min: number, max: number): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= min && value <= max;
}

function parseTarget(raw: unknown): Parsed<ScreenTarget> {
  if (!isRecord(raw)) return fail('alvo inválido');
  const extra = onlyKeys(raw, [
    'id',
    'label',
    'requires',
    'openedBy',
    'dependsOn',
    'disabledReason',
  ]);
  if (extra) return fail(extra);
  if (typeof raw.id !== 'string' || raw.id.length > 80 || !TARGET_ID.test(raw.id))
    return fail('identificador de alvo inválido');
  if (typeof raw.label !== 'string' || !raw.label.trim() || raw.label.length > SCREEN_LIMITS.label)
    return fail('rótulo de alvo inválido');
  const target: ScreenTarget = { id: raw.id, label: raw.label.trim() };
  if (raw.requires !== undefined) {
    if (
      !Array.isArray(raw.requires) ||
      raw.requires.length > 4 ||
      raw.requires.some((item) => typeof item !== 'string' || !REQUIRES.test(item))
    )
      return fail('requisito de alvo inválido');
    target.requires = raw.requires as string[];
  }
  if (raw.openedBy !== undefined) {
    if (typeof raw.openedBy !== 'string' || !TARGET_ID.test(raw.openedBy))
      return fail('alvo de abertura inválido');
    target.openedBy = raw.openedBy;
  }
  if (raw.dependsOn !== undefined) {
    const depends = raw.dependsOn;
    if (
      !isRecord(depends) ||
      onlyKeys(depends, ['target', 'value']) ||
      typeof depends.target !== 'string' ||
      !TARGET_ID.test(depends.target) ||
      typeof depends.value !== 'string' ||
      depends.value.length > 40
    )
      return fail('dependência de alvo inválida');
    target.dependsOn = { target: depends.target, value: depends.value };
  }
  if (raw.disabledReason !== undefined) {
    if (typeof raw.disabledReason !== 'string' || raw.disabledReason.length > SCREEN_LIMITS.reason)
      return fail('motivo de alvo inválido');
    target.disabledReason = raw.disabledReason;
  }
  return { ok: true, value: target };
}

export function parsePosCartV1(raw: unknown): Parsed<PosCartV1> {
  if (!isRecord(raw)) return fail('carrinho inválido');
  const extra = onlyKeys(raw, [
    'location',
    'lines',
    'customerRef',
    'displayedTotalCents',
    'discountTotalCents',
    'discountTotalBps',
    'payments',
  ]);
  if (extra) return fail(extra);
  if (typeof raw.location !== 'string' || !raw.location.trim() || raw.location.length > 60)
    return fail('local do carrinho inválido');
  if (!Array.isArray(raw.lines) || raw.lines.length > SCREEN_LIMITS.lines)
    return fail('itens do carrinho inválidos');
  const lines: PosCartV1['lines'] = [];
  for (const line of raw.lines) {
    if (!isRecord(line)) return fail('item inválido');
    const lineExtra = onlyKeys(line, ['productId', 'variantId', 'quantity', 'discountBps']);
    if (lineExtra) return fail(lineExtra);
    if (typeof line.productId !== 'string' || !UUID.test(line.productId))
      return fail('produto do item inválido');
    if (typeof line.variantId !== 'string' || !UUID.test(line.variantId))
      return fail('variação do item inválida');
    if (!isInt(line.quantity, 1, 100000)) return fail('quantidade inválida');
    if (!isInt(line.discountBps, 0, 10000)) return fail('desconto inválido');
    // Tudo e casado por variantId (patch, diff, desfazer): repetido e ambiguo, entao e recusado.
    const variantKey = (line.variantId as string).toLowerCase();
    if (lines.some((existing) => existing.variantId === variantKey))
      return fail('variação repetida no carrinho');
    lines.push({
      productId: line.productId.toLowerCase(),
      variantId: line.variantId.toLowerCase(),
      quantity: line.quantity,
      discountBps: line.discountBps,
    });
  }
  const customerRef = raw.customerRef;
  if (customerRef !== null && (typeof customerRef !== 'string' || customerRef.length > 160))
    return fail('cliente do carrinho inválido');
  if (!isInt(raw.displayedTotalCents, 0, Number.MAX_SAFE_INTEGER)) return fail('total inválido');
  if (raw.discountTotalCents !== undefined && !isInt(raw.discountTotalCents, 0, Number.MAX_SAFE_INTEGER))
    return fail('desconto no total inválido');
  if (raw.discountTotalBps !== undefined && !isInt(raw.discountTotalBps, 0, 10000))
    return fail('desconto no total inválido');
  if (raw.discountTotalCents !== undefined && raw.discountTotalBps !== undefined)
    return fail('desconto no total em reais e em percentual ao mesmo tempo');
  if (!Array.isArray(raw.payments) || raw.payments.length > SCREEN_LIMITS.payments)
    return fail('pagamentos inválidos');
  const payments: PosCartV1['payments'] = [];
  for (const payment of raw.payments) {
    if (!isRecord(payment)) return fail('pagamento inválido');
    const paymentExtra = onlyKeys(payment, [
      'method',
      'accountId',
      'cardMethodId',
      'amountCents',
      'tenderedCents',
      'installments',
      'received',
      'creditId',
    ]);
    if (paymentExtra) return fail(paymentExtra);
    if (!['cash', 'pix', 'card', 'credit', 'fiado'].includes(payment.method as string))
      return fail('forma de pagamento inválida');
    const uuidOrNull = (value: unknown): value is string | null =>
      value === null || (typeof value === 'string' && UUID.test(value));
    if (!uuidOrNull(payment.accountId)) return fail('conta do pagamento inválida');
    if (!uuidOrNull(payment.creditId)) return fail('crédito do pagamento inválido');
    const cardMethodId = payment.cardMethodId;
    if (cardMethodId !== null && (typeof cardMethodId !== 'string' || cardMethodId.length > 60))
      return fail('método de cartão inválido');
    for (const key of ['amountCents', 'tenderedCents'] as const) {
      const value = payment[key];
      if (value !== null && !isInt(value, 0, Number.MAX_SAFE_INTEGER))
        return fail('valor do pagamento inválido');
    }
    if (!isInt(payment.installments, 1, 60)) return fail('parcelas inválidas');
    if (typeof payment.received !== 'boolean') return fail('recebimento inválido');
    payments.push({
      method: payment.method as PosCartV1['payments'][number]['method'],
      accountId: payment.accountId,
      cardMethodId,
      amountCents: payment.amountCents as number | null,
      tenderedCents: payment.tenderedCents as number | null,
      installments: payment.installments,
      received: payment.received,
      creditId: payment.creditId,
    });
  }
  return {
    ok: true,
    value: {
      location: raw.location.trim(),
      lines,
      customerRef: customerRef === null ? null : (customerRef as string),
      displayedTotalCents: raw.displayedTotalCents,
      ...(raw.discountTotalCents !== undefined
        ? { discountTotalCents: raw.discountTotalCents as number }
        : {}),
      ...(raw.discountTotalBps !== undefined ? { discountTotalBps: raw.discountTotalBps as number } : {}),
      payments,
    },
  };
}

/** Valida e normaliza o rascunho de um esquema conhecido. */
export function parseScreenDraft(
  schema: string,
  data: unknown,
): Parsed<{ schema: ScreenDraftSchema; data: PosCartV1 }> {
  if (schema === 'pos.cart.v1') {
    const parsed = parsePosCartV1(data);
    return parsed.ok ? { ok: true, value: { schema, data: parsed.value } } : parsed;
  }
  return fail('esquema de rascunho desconhecido');
}

/**
 * Valida o contexto inteiro. Estrito: chave desconhecida, versao diferente,
 * tipo errado ou rascunho fora do esquema recusam o conjunto — um contexto
 * parcialmente aceito seria um contexto que ninguem conferiu.
 */
export function parseScreenContext(raw: unknown): Parsed<ScreenContextDto> {
  if (!isRecord(raw)) return fail('contexto inválido');
  const extra = onlyKeys(raw, [
    'v',
    'route',
    'module',
    'view',
    'record',
    'period',
    'draft',
    'targets',
    'capabilities',
    'contextOff',
  ]);
  if (extra) return fail(extra);
  if (raw.v !== SCREEN_CONTEXT_VERSION) return fail('versão de contexto desconhecida');
  if (
    typeof raw.route !== 'string' ||
    raw.route.length > SCREEN_LIMITS.routeLength ||
    !ROUTE.test(raw.route)
  )
    return fail('rota inválida');
  if (typeof raw.module !== 'string' || raw.module.length > 60 || !MODULE_KEY.test(raw.module))
    return fail('módulo inválido');
  // A rota de uma tela do catalogo so combina com o modulo dessa tela: quem troca um dos dois
  // nao leva o Rimati a ler outra tela (rotas fora do catalogo seguem aceitas).
  const catalogEntry = screenModuleForRoute(raw.route);
  if (catalogEntry && catalogEntry.entry.module !== raw.module)
    return fail('módulo não corresponde à rota');
  const context: ScreenContextDto = {
    v: SCREEN_CONTEXT_VERSION,
    route: normalizeScreenRoute(raw.route),
    module: raw.module,
    targets: [],
    capabilities: { patchSchemas: [], confirmTools: [] },
  };
  if (raw.contextOff !== undefined) {
    if (raw.contextOff !== true) return fail('desligamento inválido');
    context.contextOff = true;
  }
  if (raw.view !== undefined && raw.view !== null) {
    if (typeof raw.view !== 'string' || !/^[a-z][a-z0-9-]{0,29}$/.test(raw.view))
      return fail('visão inválida');
    context.view = raw.view;
  }
  if (raw.record !== undefined && raw.record !== null) {
    const record = raw.record;
    if (!isRecord(record) || onlyKeys(record, ['kind', 'id'])) return fail('registro inválido');
    if (!SCREEN_RECORD_KINDS.includes(record.kind as ScreenRecordKind))
      return fail('tipo de registro desconhecido');
    const kind = record.kind as ScreenRecordKind;
    if (typeof record.id !== 'string' || !screenRecordIdPattern(kind).test(record.id))
      return fail('identificador de registro inválido');
    context.record = { kind, id: record.id.toLowerCase() };
  }
  if (raw.period !== undefined && raw.period !== null) {
    const period = raw.period;
    if (!isRecord(period) || onlyKeys(period, ['from', 'to'])) return fail('período inválido');
    const from = typeof period.from === 'string' ? dayNumber(period.from) : null;
    const to = typeof period.to === 'string' ? dayNumber(period.to) : null;
    if (from === null || to === null || from > to || to - from > SCREEN_LIMITS.periodDays)
      return fail('período inválido');
    context.period = { from: period.from as string, to: period.to as string };
  }
  if (raw.draft !== undefined && raw.draft !== null) {
    const draft = raw.draft;
    if (!isRecord(draft) || onlyKeys(draft, ['schema', 'data']) || typeof draft.schema !== 'string')
      return fail('rascunho inválido');
    const parsed = parseScreenDraft(draft.schema, draft.data);
    if (!parsed.ok) return fail(`rascunho inválido: ${parsed.error}`);
    context.draft = parsed.value;
  }
  if (!Array.isArray(raw.targets) || raw.targets.length > SCREEN_LIMITS.targets)
    return fail('alvos inválidos');
  const seen = new Set<string>();
  for (const item of raw.targets) {
    const parsed = parseTarget(item);
    if (!parsed.ok) return fail(parsed.error);
    if (seen.has(parsed.value.id)) return fail('alvo repetido');
    seen.add(parsed.value.id);
    context.targets.push(parsed.value);
  }
  const capabilities = raw.capabilities;
  if (
    !isRecord(capabilities) ||
    onlyKeys(capabilities, ['patchSchemas', 'confirmTools']) ||
    !Array.isArray(capabilities.patchSchemas) ||
    !Array.isArray(capabilities.confirmTools)
  )
    return fail('capacidades inválidas');
  if (
    capabilities.patchSchemas.length > 5 ||
    capabilities.patchSchemas.some((s) => !SCREEN_DRAFT_SCHEMAS.includes(s as ScreenDraftSchema))
  )
    return fail('esquema de alteração desconhecido');
  if (
    capabilities.confirmTools.length > 5 ||
    capabilities.confirmTools.some((t) => typeof t !== 'string' || !TOOL.test(t))
  )
    return fail('confirmação inválida');
  context.capabilities = {
    patchSchemas: capabilities.patchSchemas as ScreenDraftSchema[],
    confirmTools: capabilities.confirmTools as string[],
  };
  return { ok: true, value: context };
}

/**
 * O contexto "desligado" por mensagem: so rota e modulo, nada do que a pessoa
 * esta fazendo. O servidor ainda sabe em que tela a mensagem foi escrita.
 */
export function withoutScreenDetail(context: ScreenContextDto): ScreenContextDto {
  return {
    v: SCREEN_CONTEXT_VERSION,
    route: context.route,
    module: context.module,
    contextOff: true,
    targets: [],
    capabilities: { patchSchemas: [], confirmTools: [] },
  };
}

/* -------------------------------------------------------------------------- */
/* Resultado da conferencia no servidor (aparece na auditoria e no prompt)     */
/* -------------------------------------------------------------------------- */

export const SCREEN_WARNINGS = [
  'module_unknown',
  'module_mismatch',
  'record_not_found',
  'record_forbidden',
  'record_kind_unsupported',
  'line_not_found',
  'customer_not_found',
  'total_differs',
  'redacted_for_role',
  'plan_unavailable',
  'draft_unavailable',
  'location_unknown',
] as const;
export type ScreenWarning = (typeof SCREEN_WARNINGS)[number];

/** O que o servidor registra de cada mensagem: ids e esquema, nunca o conteudo da tela. */
export interface ScreenContextAudit {
  v: number;
  route: string;
  module: string;
  recordRef: { kind: ScreenRecordKind; id: string } | null;
  recordAccepted: boolean;
  draftSchema: ScreenDraftSchema | null;
  /** Hash do rascunho como a pagina o enviou (normalizado), o mesmo de `baseHash`. */
  draftHash: string | null;
  /** Hash do rascunho resolvido pelo servidor (varia por papel); so auditoria. */
  resolvedDraftHash: string | null;
  draftAccepted: boolean;
  periodAccepted: boolean;
  targetsCount: number;
  contextOff: boolean;
  warnings: ScreenWarning[];
}
